<template>
  <div class="contact-form flex-grow-1 d-flex flex-column overflow-y-hidden">
    <a-form id="crawl-schedule-form" class="flex-grow-1 d-flex flex-column overflow-y-hidden" :form="crawlScheduleForm" @submit="onSubmit">
      <div class="a-header">
        <span class="a-header--title slide-down-enter-active">{{ crawlSchedule.id ? 'Edit' : 'Add' }} Crawl Schedule</span>
      </div>
      <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="isLoading">
        <Spinner />
      </div>
      <div class="a-body flex-grow-1">
        <transition-group name="slide-up">
          <template v-if="!isLoading && domains && domains.length == 0">
            <a-row :key="'info'" :gutter="20">
              <a-col :span="24">
                <p>You have not added any domains. You need to have one or more domains before you can add a crawl schedule.</p>
              </a-col>
            </a-row>
          </template>
          <template v-if="!isLoading && domains && domains.length > 0">
            <a-row :key="'info'" :gutter="20">
              <a-col :span="12">
                <a-form-item label="Select Domain">
                  <a-select
                    v-decorator="[
                      'domainId',
                      {
                        rules: [
                          {
                            required: true,
                            message: 'Please select a domain'
                          }
                        ]
                      }
                    ]"
                    size="large"
                  >
                    <a-select-option v-for="domain in domains" :key="domain.id">
                      {{ domain.domain_name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="URL to Start From">
                  <a-input
                    v-decorator="[
                      'startUrl',
                      {
                        rules: [
                          {
                            required: true,
                            message: 'Enter the URL to start the crawling (e.g. the URL of the home page)'
                          },
                          {
                            pattern: REGEX_URL_WITH_PROTOCOL,
                            message: 'Please input valid URL! (eg. https://www.example.com)'
                          }
                        ]
                      }
                    ]"
                    placeholder="e.g. http://www.example.com/">
                  </a-input>
                </a-form-item>
              </a-col>
            </a-row>
            <a-row :key="'info3'" :gutter="20">
              <a-col :span="12">
                <a-form-item label="Crawl Depth">
                  <a-select size="large"  v-decorator="['crawlDepth', {rules: [{required: true}]}]">
                    <a-select-option v-for="depth in crawlDepth" :key="depth">
                      {{ depth }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item>
                  <a-select size="large" v-decorator="['policy']">
                    <a-select-option :key="0">
                      -- None --
                    </a-select-option>
                    <a-select-option v-for="policy in policies" :key="policy.id">
                      {{ policy.name }}
                    </a-select-option>
                  </a-select>
                  <template slot="label">
                    Policy (Optional)
                    <a-tooltip placement="top">
                      <template slot="title">
                        Select a policy that you want to apply to all new webpages found during the crawl.
                      </template>
                      <a-icon class="explain" type="info-circle" theme="filled" />
                    </a-tooltip>
                  </template>
                </a-form-item>
              </a-col>
            </a-row>
            <a-row key="'info7'" :gutter="24">
              <a-col :span="6">
                <a-form-item label="Crawl Frequency">
                  <a-select size="large" v-decorator="['crawlFrequency', {rules: [{required: true}]}]">
                    <a-select-option v-for="freq in crawlFrequency" :key="freq">
                      {{ freq }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :span="6" v-show="selectedCrawlFrequency == 'Weekly'">
                <!--<a-form-item has-feedback>
                  <a-date-picker
                          v-decorator="['crawlStartTime', {rules: [{required: true, message: 'Start Date/Time is required'}]}]"
                          class="full-w"
                          placeholder="Select Date/Time"
                          :showTime="{ format: 'HH:mm' }"
                          :format="DATE_FORMAT.DDMMMYYYYHHmm"
                  />
                  <template slot="label">
                    Crawl Start Date/Time
                    <a-tooltip placement="top">
                      <template slot="title">
                        Sets the date and time of the crawl.<br>
                        If the crawl frequency is <b>Daily</b>, the crawling will be performed every day at the time specified.<br>
                        If the crawl frequency is <b>Weekly</b>, the crawling will be performed each week on the same day of the week and time as the selected start date/time.
                      </template>
                      <a-icon class="explain" type="info-circle" theme="filled" />
                    </a-tooltip>
                  </template>
                </a-form-item>-->
                <a-form-item>
                  <a-select size="large" v-decorator="['crawlStartDay']">
                    <a-select-option v-for="day in crawlStartDay" :key="day">
                      {{ day }}
                    </a-select-option>
                  </a-select>
                  <template slot="label">
                    Day of Week
                    <a-tooltip placement="top">
                      <template slot="title">
                        Perform the crawl on this day (if frequency is weekly)
                      </template>
                      <a-icon class="explain" type="info-circle" theme="filled" />
                    </a-tooltip>
                  </template>
                </a-form-item>
              </a-col>
              <a-col :span="3">
                <a-form-item>
                  <a-select size="large" v-decorator="['crawlStartHour']">
                    <a-select-option v-for="hour in 24" :key="hour - 1">
                      {{ (hour - 1) < 10 ? '0' : '' }}{{ hour - 1 }}
                    </a-select-option>
                  </a-select>
                  <template slot="label">
                    Hour
                    <a-tooltip placement="top">
                      <template slot="title">
                        Perform the crawl at this time on the specified day (if weekly) or every day (if daily).<br>
                        This value is set using the time zone that you have set in your account settings and converted to UTC time internally.<br>
                        This means that the crawl time does not follow changes in local time (e.g. DST).
                      </template>
                      <a-icon class="explain" type="info-circle" theme="filled" />
                    </a-tooltip>
                  </template>
                </a-form-item>
              </a-col>
              <a-col :span="3">
                <a-form-item>
                  <a-select size="large" v-decorator="['crawlStartMinute']">
                    <a-select-option v-for="minute in 60" :key="minute - 1">
                      {{ (minute - 1) < 10 ? '0' : '' }}{{ minute - 1 }}
                    </a-select-option>
                  </a-select>
                  <template slot="label">
                    Minute
                    <a-tooltip placement="top">
                      <template slot="title">
                        Perform the crawl at this time on the specified day (if weekly) or every day (if daily).<br>
                        This value is set using the time zone that you have set in your account settings and converted to UTC time internally.<br>
                        This means that the crawl time does not follow changes in local time (e.g. DST).
                      </template>
                      <a-icon class="explain" type="info-circle" theme="filled" />
                    </a-tooltip>
                  </template>
                </a-form-item>
              </a-col>
            </a-row>
            <a-row :key="'info5'"  :gutter="24">
              <a-col :span="24">
                <a-form-item>
                  <a-checkbox :checked="isOpenAdvOptions" @change="isOpenAdvOptions = !isOpenAdvOptions">
                    Advanced Options
                  </a-checkbox>
                </a-form-item>
              </a-col>
            </a-row>
            <a-row :key="'info4'" :gutter="20" v-show="isOpenAdvOptions">
              <a-col :span="12">
                <a-form-item label="User Agent">
                  <a-select size="large" v-decorator="['userAgent', {rules: [{required: true}]}]">
                    <a-select-option v-for="userAgent in userAgents" :key="userAgent.id" :value="userAgent.id">
                      {{ userAgent.alias }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
            </a-row>
            <a-row :key="'info6'" :gutter="20" v-show="isOpenAdvOptions">
              <a-col :span="12">
                <a-form-item extra="separate different keywords by a comma (,)">
                  <template slot="label">
                    Keyword(s) to include
                    <a-tooltip placement="top" :overlayStyle="{ 'max-width': 'max-content' }">
                      <template slot="title">
                        <span class="fs-10">
                          Specify the whitelisted keyword(s) that we should monitor on your webpage.<br />
                          An alert will be sent if the whitelisted keywords are missing from the page.<br />
                          Examples: company name, webpage title.
                        </span>
                      </template>
                      <a-icon type="info-circle" theme="filled" />
                    </a-tooltip>
                  </template>
                  <a-textarea class="row-6 mb-0" :rows="6" />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item extra="separate different keywords by a comma (,)">
                  <template slot="label">
                    Keyword(s) to exclude
                    <a-tooltip placement="top" :overlayStyle="{ 'max-width': 'max-content' }">
                      <template slot="title">
                        <span class="fs-10">
                          Specify what blacklisted keyword(s) that should not appear on your webpage.<br />
                          An alert will be sent if the blacklisted keywords are detected on the page.<br />
                          Examples: mysql/php error.
                        </span>
                      </template>
                      <a-icon type="info-circle" theme="filled" />
                    </a-tooltip>
                  </template>
                  <a-textarea class="row-6 mb-0" :rows="6" />
                </a-form-item>
              </a-col>
            </a-row>
          </template>
        </transition-group>
      </div>
      <div class="a-footer">
        <a-button key="back" size="large" class="mr-3 slide-down-enter-active" @click="onCancel()">
          Cancel
        </a-button>
        <a-button key="submit" size="large" type="primary" html-type="submit" class="slide-down-enter-active">
          Save
        </a-button>
      </div>
    </a-form>
  </div>
</template>

<script>
  import DomainService from "../services/Domain.service";
  import PolicyService from "../../Policy/services/policy.service"
  import { DATE_FORMAT } from '@constants/dateFormat';
  import DataService from '@shared/services/data.service';
  import { flatMapErrMessagesValidateForm, debounceTimeExecute } from '@shared/helpers/utils';
  import { REGEX_URL_WITH_PROTOCOL } from '@constants/regex';
  import moment from "moment-timezone";
  import WebpageService from "../../Webpages/services/webpage.service";
  import { DEFAULT_USER_AGENT_ID } from '../../Webpages/consts/Webpage.consts';

  export default {
    name: 'DomainCrawlScheduleForm',
    props: {
      crawlSchedule: {
        domain_id: null,
        name: null,
        start_date: null,
        interval: null,
        seed_url: null,
        depth: null,
        user_agent_id: null,
        text_string_appear: null,
        text_string_not_appear: null,
        policy_id: null
      }
    },
    data() {
      return {
        REGEX_URL_WITH_PROTOCOL,
        DATE_FORMAT,
        crawlDepth: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
        crawlFrequency: ['Weekly', 'Daily'],
        crawlStartDay: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        domains: [],
        policies: [],
        userAgents: [],
        isLoading: true,
        isOpenAdvOptions: false,
        selectedCrawlFrequency: 'Weekly',
      };
    },
    watch: {
      crawlSchedule: function (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.setCrawlSchedule();
        }
      }
    },
    beforeCreate() {
      this.crawlScheduleForm = this.$form.createForm(this, {
        name: 'crawl-schedule-form',
        mapPropsToFields: () => {
          return {
            domainId: this.$form.createFormField({
              value: ''
            }),
            startUrl: this.$form.createFormField({
              value: ''
            }),
            crawlDepth: this.$form.createFormField({
              value: 5
            }),
            crawlFrequency: this.$form.createFormField({
              value: 'Weekly'
            }),
            crawlStartDay: this.$form.createFormField({
              value: 'Sun'
            }),
            crawlStartHour: this.$form.createFormField({
              value: 0
            }),
            crawlStartMinute: this.$form.createFormField({
              value: 0
            }),
            userAgent: this.$form.createFormField({
              value: DEFAULT_USER_AGENT_ID
            }),
            policy: this.$form.createFormField({
              value: 0
            }),
            keywordsInclude: this.$form.createFormField({
              value: ''
            }),
            keywordsExclude: this.$form.createFormField({
              value: ''
            })
          };
        },
        onValuesChange: (props, values) => {
          if (values.crawlFrequency) {
            this.selectedCrawlFrequency = values.crawlFrequency;
          }
        }
      });
      this.crawlScheduleForm.getFieldDecorator('domainId', {});
      this.crawlScheduleForm.getFieldDecorator('startUrl', {});
      this.crawlScheduleForm.getFieldDecorator('crawlDepth', {});
      this.crawlScheduleForm.getFieldDecorator('crawlFrequency', {});
      this.crawlScheduleForm.getFieldDecorator('crawlStartDay', {});
      this.crawlScheduleForm.getFieldDecorator('crawlStartHour', {});
      this.crawlScheduleForm.getFieldDecorator('crawlStartMinute', {});
      this.crawlScheduleForm.getFieldDecorator('userAgent', {});
      this.crawlScheduleForm.getFieldDecorator('policy', {});
      this.crawlScheduleForm.getFieldDecorator('keywordsInclude', {});
      this.crawlScheduleForm.getFieldDecorator('keywordsExclude', {});
    },
    mounted() {
      this.getData();
    },
    methods: {
      async getData() {
        await this.getSelectorFieldsOptions();

        if (this.crawlSchedule.id) {
          this.setCrawlSchedule();
        } else {
          this.setCrawlScheduleDefaults();
        }
        this.isLoading = false;
      },
      async getSelectorFieldsOptions() {
        [this.domains, this.policies, this.userAgents] = await Promise.all([
          DomainService.fetchListOfDomainSubscribed(),
          PolicyService.getList(),
          DataService.getUserAgents()
        ]);
      },
      setCrawlSchedule() {
        this.crawlScheduleForm.setFieldsValue({
          domainId: this.crawlSchedule.domain_id,
          startUrl: this.crawlSchedule.seed_url,
          crawlDepth: this.crawlSchedule.depth,
          crawlFrequency: this.crawlSchedule.frequency,
          crawlStartDay: this.crawlSchedule.day,
          crawlStartHour: this.crawlSchedule.hour,
          crawlStartMinute: this.crawlSchedule.minute,
          userAgent: this.crawlSchedule.user_agent_id,
          policy: (this.crawlSchedule.policy_id ? this.crawlSchedule.policy_id : 0),
          keywordsInclude: this.crawlSchedule.text_string_appear,
          keywordsExclude:this.crawlSchedule.text_string_not_appear,
        });
      },
      setCrawlScheduleDefaults() {
        this.crawlScheduleForm.setFieldsValue({
          domainId: this.domains.length > 0 ? this.domains[0].id : null,
          startUrl: '',
          crawlDepth: 5,
          crawlFrequency: 'Weekly',
          crawlStartDay: 'Mon',
          crawlStartHour: 0,
          crawlStartMinute: 0,
          userAgent: this.userAgents[0].id,
          policy: 0,
          keywordsInclude: '',
          keywordsExclude: '',
        });
      },
      onCancel() {
        this.$router.push('/domain/list-crawl-schedule');
      },
      onSubmit(e) {
        e.preventDefault();
        this.crawlScheduleForm.validateFields((err, values) => {
          console.log(values);
          if (!err) {
            const bodyParams = {
              domain_id: values.domainId,
              seed_url: values.startUrl,
              depth: values.crawlDepth,
              frequency: values.crawlFrequency,
              day: values.crawlStartDay,
              hour: values.crawlStartHour,
              minute: values.crawlStartMinute,
              user_agent_id: values.userAgent,
              text_string_appear: values.keywordsInclude || '',
              text_string_not_appear: values.keywordsExclude || '',
              policy_id: values.policy || null,
            };
            try {
              this.$emit('submit', bodyParams);
            } catch (error) {
              this.$showMultiErrorMessage(error.messages);
            }
          }
          if (err) {
            debounceTimeExecute(flatMapErrMessagesValidateForm(err), this.$showErrorMessage);
          }
        });
      }
    }
  };
</script>

<style lang="scss" scoped>
  .a-body {
    padding: 20px;
  }

  .email-config {
    background-color: #f7f7f7;
    padding: 20px;
  }

  .email-config__title {
    color: #323232;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 15px;
  }
</style>
